import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  interpretLoginResponse,
  redirectAfterLogin,
  submitLogin,
  submitMfaResetRequest,
} from './loginApi';
import {
  isLockoutPayload,
  lockoutDateFromPayload,
  parseJsonResponse,
} from './loginFormUtils';
import type {
  LockoutState,
  LoginStep,
  LoginResponseBody,
  MfaVerificationChallenge,
} from './loginFormTypes';

export type MfaResetStatus =
  | 'idle'
  | 'sending'
  | 'sent'
  | 'already_pending'
  | 'error';

export const useLoginForm = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challengeCode, setChallengeCode] = useState('');
  const [error, setError] = useState('');
  const [lockout, setLockout] = useState<LockoutState | null>(null);
  const [step, setStep] = useState<LoginStep>('login');
  const [challengeType, setChallengeType] =
    useState<MfaVerificationChallenge | null>(null);
  const [mfaResetStatus, setMfaResetStatus] = useState<MfaResetStatus>('idle');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (step === 'login' && (!email || !password)) {
      setError(t('auth.errors.email_password_required'));
      return;
    }

    setLoading(true);

    try {
      const response = await submitLogin({ email, password, challengeCode });
      const parsed = await parseJsonResponse(response);

      if (!parsed.ok) {
        setError(t(parsed.errorKey));
        return;
      }

      const data = parsed.data as LoginResponseBody;

      if (response.status === 429 && isLockoutPayload(data)) {
        const lockedUntil = lockoutDateFromPayload(data);
        if (!lockedUntil) {
          setError(data.error);
          return;
        }

        setError('');
        setLockout({ message: data.error, lockedUntil });
        return;
      }

      // Valid password but unverified email: send the user to finish
      // verification (email prefilled) instead of dead-ending on an error.
      if (data.email_verification_required) {
        window.location.href = `/confirm-registration?email=${encodeURIComponent(email)}`;
        return;
      }

      const outcome = interpretLoginResponse(response, data);

      switch (outcome.type) {
        case 'error':
          setError(outcome.message);
          break;
        case 'challenge':
          setPassword('');
          setStep('challenge');
          setChallengeType(outcome.challengeType);
          break;
        case 'success':
          await redirectAfterLogin();
          break;
      }
    } catch (submitError) {
      console.error(submitError);
      setError(t('auth.errors.unexpected'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setStep('login');
    setChallengeCode('');
    setChallengeType(null);
    setError('');
    setMfaResetStatus('idle');
  };

  const handleRequestMfaReset = async () => {
    setMfaResetStatus('sending');
    setError('');

    try {
      const response = await submitMfaResetRequest();

      if (!response.ok) {
        setMfaResetStatus('error');
        return;
      }

      const data = await response.json();
      setMfaResetStatus(data.already_pending ? 'already_pending' : 'sent');
    } catch (requestError) {
      console.error(requestError);
      setMfaResetStatus('error');
    }
  };

  const handleLockoutExpire = () => {
    setLockout(null);
  };

  return {
    showPassword,
    setShowPassword,
    loading,
    email,
    setEmail,
    password,
    setPassword,
    challengeCode,
    setChallengeCode,
    error,
    lockout,
    step,
    challengeType,
    isLocked: lockout !== null,
    mfaResetStatus,
    handleSubmit,
    handleBackToLogin,
    handleLockoutExpire,
    handleRequestMfaReset,
    t,
  };
};
